import { DownloadOutlined } from '@ant-design/icons';
import { Card, Col, Row, Space, Typography } from 'antd';
import { HeroPanel } from '../../components/shared/HeroPanel';
import { HWButton } from '../../components/shared/StyledButton';
import { MfaCodeGenerator } from './mfa-code-generator';

const userScriptPath = '/userscripts/housewhisper-fub-login.user.js';
const downloadPath = '/extensions/housewhisper-fub-containers-0.2.1.xpi';

export function ResourcesPage(): JSX.Element {
  const firefoxVersion = navigator.userAgent.match(/Firefox\/(\d+)/)?.[1];
  const isDesktopFirefox =
    !!firefoxVersion &&
    !/Android|Mobile|Tablet|iPad|iPhone/.test(navigator.userAgent);
  const canInstallContainers =
    isDesktopFirefox && Number(firefoxVersion) >= 142;
  const tampermonkeyUrl = navigator.userAgent.includes('Firefox')
    ? 'https://addons.mozilla.org/en-US/firefox/addon/tampermonkey/'
    : /Chrome|Chromium/.test(navigator.userAgent)
      ? 'https://chromewebstore.google.com/detail/tampermonkey/dhdgffkkebhmkfjojejmpbldmpobfkfo'
      : 'https://www.tampermonkey.net/';

  return (
    <HeroPanel
      maxWidth={1120}
      subtitle="Tools for signing in to FUB and managing client sessions."
      title="Resources"
    >
      <Row align="stretch" gutter={[24, 24]}>
        <Col md={12} xs={24}>
          <Card style={{ textAlign: 'left', height: '100%' }}>
            <Typography.Title level={4}>FUB login helper</Typography.Title>
            <Typography.Paragraph type="secondary">
              Log in to Follow Up Boss with your client&apos;s saved login
              details.
            </Typography.Paragraph>

            <HWButton
              block
              icon={<DownloadOutlined />}
              link={tampermonkeyUrl}
              rel="noreferrer"
              size="large"
              style={{ height: 48 }}
              target="_blank"
              type="primary"
            >
              1. Install Tampermonkey
            </HWButton>
            <ul style={{ paddingLeft: 20, marginTop: 16, marginBottom: 24 }}>
              <li>
                Chrome: find the Tampermonkey icon under Extensions (the puzzle
                piece) if hidden. Right-click it, select Manage extension, and
                enable Allow User Scripts.
              </li>
              <li>
                Firefox: install the{' '}
                <a
                  href="https://addons.mozilla.org/en-US/firefox/addon/tampermonkey/"
                  rel="noreferrer"
                  target="_blank"
                >
                  official Tampermonkey add-on
                </a>
                , click Add to Firefox, then confirm Add.
              </li>
            </ul>

            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <HWButton
                block
                icon={<DownloadOutlined />}
                link={userScriptPath}
                size="large"
                style={{ height: 48 }}
                type="primary"
              >
                2. Install HouseWhisper FUB Login Helper
              </HWButton>
            </Space>
            <Typography.Paragraph style={{ marginTop: 16, marginBottom: 0 }}>
              Log in via FUB.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col md={12} xs={24}>
          <Card style={{ textAlign: 'left', height: '100%' }}>
            <Typography.Title level={4}>
              FUB Firefox containers
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              Stay signed in to multiple Follow Up Boss accounts at the same
              time. Open each client’s subdomain in Firefox, and its account
              gets its own container automatically.
            </Typography.Paragraph>
            {!canInstallContainers && (
              <Typography.Paragraph type="warning">
                {isDesktopFirefox
                  ? 'Update Firefox to version 142 or later to install.'
                  : 'Open this page in Firefox on your computer to install.'}
              </Typography.Paragraph>
            )}
            <HWButton
              block
              disabled={!canInstallContainers}
              icon={<DownloadOutlined />}
              link={canInstallContainers ? downloadPath : undefined}
              size="large"
              style={{ height: 48 }}
              type="primary"
            >
              Install Firefox containers
            </HWButton>
            <ol style={{ paddingLeft: 20, marginTop: 16, marginBottom: 24 }}>
              <li>
                Use the button above, then choose{' '}
                <Typography.Text strong>Add</Typography.Text> when Firefox asks
                to install HouseWhisper FUB Containers.
              </li>
              <li>
                Open each client’s FUB subdomain and sign in with that client’s
                account. Switch between tabs without signing out.
              </li>
            </ol>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              If Firefox downloads a file instead, open{' '}
              <Typography.Text code>about:addons</Typography.Text>, select the
              gear menu, then{' '}
              <Typography.Text strong>Install Add-on From File</Typography.Text>{' '}
              and choose the downloaded file.
            </Typography.Paragraph>
          </Card>
        </Col>
        <Col md={12} xs={24}>
          <MfaCodeGenerator />
        </Col>
      </Row>
    </HeroPanel>
  );
}
